// zod-based validation of the trigger ConditionNode tree. Enforces decimal-string
// values, percentage-point fields, required fields per family, db_news "at least one
// facet" rule, group arity (>=2 children), and a recursion depth cap. Run before
// building the proto Condition in submitTriggerAlgoOrder; returns the first error
// message (for a toast) or null when the tree is valid.
import { z } from 'zod'

import type { ConditionNode, LeafConditionNode } from './trigger-condition-model'

export const MAX_CONDITION_DEPTH = 3

const decimalRe = /^-?\d+(\.\d+)?$/

function normNum(s: string): string {
  return s.replace(/,/g, '').trim()
}

/** Decimal string (thousands separators allowed); `signed` permits a leading minus. */
function numeric(message: string, opts: { signed?: boolean; positive?: boolean } = {}) {
  return z.string().superRefine((s, ctx) => {
    const t = normNum(s)
    if (!decimalRe.test(t)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message })
      return
    }
    if (opts.signed === false && t.startsWith('-')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${message} (must not be negative)` })
    }
    if (opts.positive && !(Number(t) > 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${message} (must be greater than 0)` })
    }
  })
}

const positiveInt = (message: string) =>
  z.string().refine((s) => {
    const n = Number(normNum(s))
    return Number.isInteger(n) && n > 0
  }, message)

const indicatorSpecSchema = z.object({
  indicatorType: z.enum(['sma', 'ema']),
  symbol: z.string(),
  source: z.string(),
  periodCount: positiveInt('Period must be a positive integer'),
  timeframeSeconds: positiveInt('Timeframe is required'),
})

const priceSchema = z
  .object({
    value: z.string(),
    relative: z
      .object({ anchor: z.string(), offset: z.string(), offsetUnit: z.string() })
      .optional(),
  })
  .superRefine((v, ctx) => {
    const valuePresent = v.value.trim() !== ''
    const relPresent = !!v.relative
    if (!valuePresent && !relPresent) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a trigger price' })
      return
    }
    if (valuePresent && relPresent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Set either an absolute price or a relative offset (not both)',
      })
      return
    }
    if (valuePresent && !decimalRe.test(normNum(v.value))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Price must be a number' })
    }
    if (relPresent && !decimalRe.test(normNum(v.relative!.offset))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Offset must be a number' })
    }
  })

const technicalIndicatorSchema = z
  .object({
    left: indicatorSpecSchema,
    targetMode: z.enum(['threshold', 'indicator']),
    threshold: z.string(),
    right: z.unknown(),
  })
  .superRefine((v, ctx) => {
    if (v.targetMode === 'indicator') {
      const r = indicatorSpecSchema.safeParse(v.right)
      if (!r.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: r.error.issues[0]?.message ?? 'Invalid comparison indicator',
        })
      }
    } else if (!decimalRe.test(normNum(v.threshold))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Threshold must be a number' })
    }
  })

const returnsSchema = z.object({
  windowMs: positiveInt('Lookback window is required'),
  valuePercent: numeric('Return percent must be a number'),
  timeframeSeconds: positiveInt('Timeframe is required'),
})

const candleSchema = z.object({
  timeframeSeconds: positiveInt('Timeframe is required'),
  value: numeric('Candle value must be a number'),
})

const portfolioMetricSchema = z
  .object({
    metric: z.enum(['account_value_usd', 'currency_balance']),
    currency: z.string(),
    value: numeric('Value must be a number'),
  })
  .superRefine((v, ctx) => {
    if (v.metric === 'currency_balance' && v.currency.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a currency' })
    }
  })

const positionStatsSchema = z
  .object({
    scopeKind: z.enum(['symbol', 'underlying', 'group', 'all']),
    scopeValue: z.string(),
    value: numeric('Value must be a number'),
  })
  .superRefine((v, ctx) => {
    if (v.scopeKind !== 'all' && v.scopeValue.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a position scope target' })
    }
  })

const riskMetricSchema = z.object({
  asset: z.string().trim().min(1, 'Asset is required'),
  value: numeric('Value must be a number'),
})

const optionIvSchema = z.object({
  symbol: z.string().trim().min(1, 'Option instrument is required'),
  underlying: z.string().trim().min(1, 'Underlying is required'),
  valuePercent: numeric('IV must be a number', { signed: false, positive: true }),
})

const scheduleSchema = z
  .object({ variant: z.enum(['fire_at', 'delay']), value: z.string() })
  .superRefine((v, ctx) => {
    if (v.variant === 'delay') {
      const n = Number(normNum(v.value))
      if (!(n > 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Delay must be greater than 0 seconds',
        })
      }
      return
    }
    const ms = Number(v.value)
    if (!Number.isFinite(ms) || ms <= Date.now()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose a time in the future' })
    }
  })

const dbNewsSchema = z
  .object({
    eeType: z.array(z.string()),
    newsType: z.array(z.string()),
    coins: z.array(z.string()),
    tags: z.array(z.string()),
    textContains: z.array(z.string()),
  })
  .superRefine((v, ctx) => {
    const hasAny = [v.eeType, v.newsType, v.coins, v.tags, v.textContains].some((g) =>
      g.some((s) => s.trim() !== '')
    )
    if (!hasAny) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add at least one news filter (ee type, news type, coin, tag, or text)',
      })
    }
  })

const actionEventSchema = z.object({
  targetValue: z.string().trim().min(1, 'Target is required'),
})

const LEAF_SCHEMAS: Record<LeafConditionNode['kind'], z.ZodTypeAny> = {
  price: priceSchema,
  technical_indicator: technicalIndicatorSchema,
  returns: returnsSchema,
  candle: candleSchema,
  portfolio_metric: portfolioMetricSchema,
  position_stats: positionStatsSchema,
  risk_metric: riskMetricSchema,
  option_iv: optionIvSchema,
  schedule: scheduleSchema,
  db_news: dbNewsSchema,
  action_event: actionEventSchema,
}

/**
 * Validate a condition tree. Returns the first error message, or null when valid.
 * `depth` counts group nesting levels (root group = 1); trees deeper than
 * `maxDepth` are rejected to match the alert-service validator's tree-depth cap.
 */
export function validateConditionTree(
  node: ConditionNode,
  opts: { maxDepth?: number } = {},
  depth = 1
): string | null {
  const maxDepth = opts.maxDepth ?? MAX_CONDITION_DEPTH

  if (node.kind === 'and' || node.kind === 'or') {
    if (depth > maxDepth) {
      return `Conditions can be nested at most ${maxDepth} levels deep`
    }
    if (node.children.length < 2) {
      return 'A group needs at least two conditions'
    }
    for (const child of node.children) {
      const childError = validateConditionTree(child, opts, depth + 1)
      if (childError) return childError
    }
    return null
  }

  const schema = LEAF_SCHEMAS[node.kind]
  const result = schema.safeParse(node)
  return result.success ? null : (result.error.issues[0]?.message ?? 'Invalid condition')
}
